import React from 'react';
import { Link } from 'react-router-dom';
import EmailSignup from '../components/EmailSignup';

const About = () => {
  return (
    <div className="page about-page">
      <div className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <h1>Turn SharePoint GIS Files into Interactive Web&nbsp;Maps - Seamlessly</h1>
            <p className="hero-subtitle">
              <strong>ShareShape</strong> connects directly to your SharePoint site so you can share up-to-date, interactive maps with your team or clients - no exporting, converting, or rehosting required.
            </p>
            <div className="hero-buttons">
              <Link to="/map" className="btn btn-primary">
                Launch App
              </Link>
              <Link to="/map/demo" className="btn btn-secondary">
                View Demo
              </Link>
            </div>
          </div>
          <div className="hero-visual">
            <div className="transformation-flow">
              <div className="flow-item">
                <img src="/images/hero-1.PNG" alt="SharePoint Files" className="flow-image" />
                <span className="flow-label">Geodata on SharePoint</span>
              </div>
              <div className="flow-arrow">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 120">
                  <defs>
                    <linearGradient id="arrowGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" style={{stopColor:"#ffffff", stopOpacity:"0.05"}} />
                      <stop offset="70%" style={{stopColor:"#ffffff", stopOpacity:"0.7"}} />
                      <stop offset="100%" style={{stopColor:"#ffffff", stopOpacity:"0.95"}} />
                    </linearGradient>
                  </defs>
                  <polygon points="8,10 8,110 42,60" fill="url(#arrowGrad)" />
                </svg>
              </div>
              <div className="flow-item">
                <img src="/images/hero-3.PNG" alt="Interactive Map" className="flow-image" />
                <span className="flow-label">Shareable Web Maps</span>
              </div>
            </div>
          </div>
        </div>
      </div>


      <div className="process-flow-section">
        <div className="container">
          <h2>How ShareShape Works</h2>
          <div className="process-flow">
            
            {/* Map Creator Role */}
            <div className="role-section">
              <div className="role-header">
                <h3>Map Creator</h3>
                <p>Create and design interactive maps</p>
              </div>
              <div className="steps">
                <div className="step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h4>🔐 Sign in with Microsoft</h4>
                    <p>No new accounts to manage.</p>
                  </div>
                </div>
                <div className="step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h4>📂 Browse & select files</h4>
                    <p>Use shapefiles, GeoTIFFs, and more without exporting.</p>
                  </div>
                </div>
                <div className="step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <h4>🎨 Style your map</h4>
                    <p>Customize layers, colors, and labels instantly.</p>
                  </div>
                </div>
                <div className="step">
                  <div className="step-number">4</div>
                  <div className="step-content">
                    <h4>🔗 Share securely</h4>
                    <p>Send a link with optional password protection.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Share Transition Arrow */}
            <div className="share-transition">
              <div className="share-arrow">
                <svg width="60" height="30" viewBox="0 0 60 30" fill="none">
                  <path d="M5 15L55 15M45 5L55 15L45 25" stroke="#0078d4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <p>Share Link</p>
            </div>

            {/* Map Viewer Role */}
            <div className="role-section">
              <div className="role-header">
                <h3>Map Viewer</h3>
                <p>Explore and interact</p>
              </div>
              <div className="steps">
                <div className="step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h4>🔗 Open the shared map</h4>
                    <p>Works on desktop and mobile.</p>
                  </div>
                </div>
                <div className="step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h4>🗺️ Explore live data</h4>
                    <p>Toggle layers, view legends, and zoom - no software install.</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <div className="personal-intro-section">
        <div className="container">
          <h2>A lightweight way to connect maps and SharePoint.</h2>
          <div className="intro-text">
            <p>ShareShape makes it easy to create and share GIS maps - straight from SharePoint - with clients, colleagues, or the public.</p>
            
            <p>ShareShape reads geospatial files and images directly from your SharePoint site, allowing you to build interactive maps without manual data transfer. Since the connection is live, your maps can remain up to date as your files change - no versioning headaches.</p>
          </div>
        </div>
      </div>

      <div className="app-preview-section">
        <div className="container">
          <img 
            src="/images/annotated-app-preview.png" 
            alt="ShareShape application interface showing geospatial data from SharePoint on an interactive map" 
            className="app-preview-image"
          />
        </div>
      </div>

      <div className="email-signup-section">
        <div className="container">
          <h3>If that sounds useful,</h3>
          <p>Join the email list to get updates as we get closer to launch.</p>
          <EmailSignup />
        </div>
      </div>

      <div className="contact-section">
        <div className="container">
          <h3>Questions or Need Help?</h3>
          <p>Get in touch</p>
          <div className="contact-buttons">
            <button 
              className="btn btn-primary" 
              onClick={() => {
                const email = ['contact', '@', 'shareshape', '.', 'com'].join('');
                window.location.href = `mailto:${email}`;
              }}
            >
              Contact Us
            </button>
          </div>
        </div>
      </div>


      <div className="cta-section">
        <div className="container">
          <h2>Ready to Get Started?</h2>
          <p>Transform your SharePoint geospatial data into powerful interactive visualizations</p>
          <div className="cta-buttons">
            <Link to="/map" className="btn btn-primary btn-large">
              Launch App
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;